import { MoveRight, Search } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import kicksDanificado from '@/assets/hero/kicks-danificado.webp'
import kicks from '@/assets/hero/kicks.webp'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { normalizePlate } from '@/lib/plate'
import { BeforeAfter } from './BeforeAfter'

/*
  Base: "Hero with text and two button", de tommyjepsen (21st.dev), instalado e
  adaptado — o par de botões deu lugar a um único campo com um botão primário,
  porque é a placa que faz a conversão. O texto fica à esquerda; à direita, o
  mesmo carro do anúncio e, arrastando, os problemas que ele pode esconder.
*/

interface PlateHeroProps {
  onSubmit: (plate: string) => void
  loading: boolean
  error: string | null
}

export function PlateHero({ onSubmit, loading, error }: PlateHeroProps) {
  const [value, setValue] = useState('')

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSubmit(normalizePlate(value))
  }

  return (
    <section id="consultar" className="w-full bg-background">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-4 py-10 sm:py-16 lg:grid-cols-2 lg:gap-12 lg:py-20">
        <div className="flex flex-col items-start gap-6 sm:gap-7">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft px-4 py-1.5 text-xs font-semibold text-brand-ink">
            <Search className="size-3.5" aria-hidden />
            Consulta grátis, sem cadastro
          </span>

          <div className="flex flex-col gap-4">
            <h1 className="text-4xl leading-[1.05] font-extrabold tracking-tight text-foreground md:text-5xl xl:text-[3.5rem]">
              Quer consultar um veículo pela <span className="text-brand-logo">placa</span>?
            </h1>
            <p className="max-w-lg text-lg leading-relaxed text-muted-foreground md:text-xl">
              Digite a placa e veja o histórico antes de fechar negócio.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex w-full max-w-xl flex-col gap-3" noValidate>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                value={value}
                onChange={(event) => setValue(event.target.value.toUpperCase())}
                placeholder="Digite a placa"
                aria-label="Placa do veículo"
                aria-invalid={error !== null}
                aria-describedby={error ? 'plate-error' : undefined}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={8}
                className="font-display flex-1 text-lg font-bold tracking-[0.15em] uppercase placeholder:text-base placeholder:font-medium"
              />
              <Button type="submit" size="lg" disabled={loading} className="sm:w-auto">
                {loading ? 'Consultando…' : 'Consultar grátis'}
                {!loading && <MoveRight className="size-4" aria-hidden />}
              </Button>
            </div>

            {/* O erro de formato aparece embaixo do campo, nunca em alerta. */}
            {error && (
              <p id="plate-error" role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            )}
          </form>
        </div>

        <BeforeAfter
          width={1200}
          height={896}
          before={{
            src: kicks,
            alt: 'Nissan Kicks prata numa garagem, em bom estado, como aparece no anúncio',
            label: 'No anúncio',
          }}
          after={{
            src: kicksDanificado,
            alt: 'O mesmo Nissan Kicks com vidros estilhaçados, para-brisa trincado, portas riscadas e pneu dianteiro furado',
            label: 'Problemas ocultos',
          }}
          className="w-full shadow-xl shadow-black/10"
        />
      </div>
    </section>
  )
}
